import React from "react";
import { BeakerIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Badge } from "../../../ui/Badge";
import { Button } from "../../../ui/Button";
import { SCORE_TYPE_LABEL, scoreTypeFor, splitCriteria } from "../utils/criteria";

interface EvaluatorsEmptyStateProps {
  /** Opens the create form pre-filled with this criterion. */
  onStartFrom: (criteria: string) => void;
  onStartBlank: () => void;
}

/**
 * Starters covering all three shapes a criterion takes: reward something good, require
 * something outright, penalise something bad. Between them they also demonstrate both
 * score types, which is the thing new users most often get wrong.
 */
const STARTERS = [
  "Reward responses that cite a source for every factual claim",
  "Passes if the response answers the question without inventing policy details",
  "Penalize responses that promise delivery dates the tools did not confirm",
];

/**
 * Shown when an organisation has no evaluators yet.
 *
 * This used to be the string "No evaluators yet." — the entire onboarding for the page
 * that defines what the product measures. Writing a criterion that discriminates is the
 * hardest task in the product, so the empty state now hands over three working examples
 * rather than a blank textarea.
 */
export function EvaluatorsEmptyState({
  onStartFrom,
  onStartBlank,
}: EvaluatorsEmptyStateProps) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-2xl mx-auto px-6 py-12 flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <BeakerIcon className="w-10 h-10 text-primary" />
          <h2 className="text-xl font-bold text-foreground">
            Define what &ldquo;good&rdquo; means for your agent
          </h2>
          <p className="text-sm text-muted-foreground">
            An evaluator is one criterion, written in plain English. Composo scores every
            trace you send against it and explains each judgement. Start from an example
            below, or write your own.
          </p>
        </div>

        <ul className="flex flex-col gap-2">
          {STARTERS.map((criteria) => {
            const { prefix } = splitCriteria(criteria);
            const scoreType = prefix ? scoreTypeFor(prefix) : null;
            return (
              <li key={criteria}>
                <button
                  type="button"
                  onClick={() => onStartFrom(criteria)}
                  className="w-full text-left rounded-md border border-border bg-card p-3 hover:bg-accent hover:border-primary/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex items-center gap-2 mb-1.5">
                    {scoreType && (
                      <Badge variant="outline">{SCORE_TYPE_LABEL[scoreType]}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Use this as a starting point
                    </span>
                  </span>
                  <span className="block font-mono text-sm text-foreground">
                    {criteria}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div>
          <Button variant="primary" size="sm" onClick={onStartBlank}>
            <PlusIcon className="w-4 h-4" />
            Write my own
          </Button>
        </div>
      </div>
    </div>
  );
}
